import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Lightning, Rocket, Crown, Check, ArrowRight, X, Lock, CreditCard, Coins } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const UPGRADE_TIERS = [
  {
    code: "creator",
    label: "Creator",
    price: 49,
    icon: Rocket,
    features: ["500 gen/mo", "10 workspaces", "Brief builder", "Content calendar"],
  },
  {
    code: "studio",
    label: "Studio",
    price: 149,
    icon: Crown,
    highlight: true,
    features: ["Unlimited gen*", "50 workspaces", "Content planner", "Client portal", "Reports"],
  },
];

const CREDIT_PACKS = [
  { id: "pack_100", credits: 100, price: 20, label: "100 Credits" },
  { id: "pack_250", credits: 250, price: 50, label: "250 Credits" },
  { id: "pack_500", credits: 500, price: 200, label: "500 Credits" },
  { id: "pack_1000", credits: 1000, price: 500, label: "1,000 Credits" },
];

const FEATURE_NAMES = {
  brand_voice_wizard: "Brand Voice AI Wizard",
  brief_builder: "Content Brief Builder",
  content_calendar: "Content Calendar",
  status_workflow: "Status Workflow",
  per_section_regenerate: "Per-Section Regenerate",
  content_planner: "Monthly Content Planner",
  client_approval_portal: "Client Approval Portal",
  report_builder: "Client Report Builder",
  insights_dashboard: "Usage Insights Dashboard",
  client_viewer_role: "Client Viewer Role",
};

const FEATURE_MIN_TIER = {
  brand_voice_wizard: "Creator",
  brief_builder: "Creator",
  content_calendar: "Creator",
  status_workflow: "Creator",
  per_section_regenerate: "Creator",
  content_planner: "Studio",
  client_approval_portal: "Studio",
  report_builder: "Studio",
  insights_dashboard: "Studio",
  client_viewer_role: "Studio",
};

export const UpgradeModal = ({ isOpen, onClose, trigger = "generate content", errorDetail = null }) => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [view, setView] = useState("plans"); // "plans" | "credits"
  const [purchasing, setPurchasing] = useState(null);

  const isFeatureGate = errorDetail?.error === "feature_not_available";
  const isGenLimit = errorDetail?.error === "generation_limit_reached";
  const featureName = FEATURE_NAMES[errorDetail?.feature] || errorDetail?.feature || "this feature";
  const minTier = FEATURE_MIN_TIER[errorDetail?.feature] || "Creator";
  const creditBalance = errorDetail?.credit_balance || 0;

  const handleBuyCredits = async (pack) => {
    setPurchasing(pack.id);
    try {
      const resp = await api.post("/stripe/credits/checkout", { pack_id: pack.id });
      window.location.href = resp.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Unable to start checkout");
      setPurchasing(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => { if (!v) { onClose(); setView("plans"); } }}>
      <DialogContent
        className="bg-[var(--bg-card)] border-[var(--border-strong)] text-[var(--text-primary)] sm:max-w-lg p-0 gap-0 overflow-hidden"
        data-testid="upgrade-modal"
      >
        <DialogTitle className="sr-only">Upgrade Your Plan</DialogTitle>

        <div className="px-6 pt-6 pb-4 text-center">
          <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-4 ${
            isFeatureGate ? "bg-amber-500/10" : "bg-red-500/10"
          }`}>
            {isFeatureGate
              ? <Lock size={28} weight="fill" className="text-amber-500" />
              : <Lightning size={28} weight="fill" className="text-red-500" />
            }
          </div>
          <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[var(--text-primary)]">
            {isFeatureGate
              ? `${featureName} is a ${minTier}+ feature`
              : "You've hit your generation limit"
            }
          </h2>
          <p className="text-sm text-[var(--text-muted)] mt-2">
            {isFeatureGate
              ? `Upgrade to ${minTier} or above to unlock ${featureName}.`
              : errorDetail?.message || "Upgrade to keep creating content this month."
            }
          </p>
          {creditBalance > 0 && isGenLimit && (
            <p className="text-xs text-amber-400 mt-1">You have {creditBalance} purchased credits remaining.</p>
          )}
        </div>

        {/* Tab toggle for gen limit */}
        {isGenLimit && (
          <div className="px-6 mb-3 flex gap-1 p-1 rounded-lg bg-[var(--bg-base)] border border-[var(--border)]">
            <button
              onClick={() => setView("plans")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-colors ${
                view === "plans" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              }`}
              data-testid="upgrade-tab-plans"
            >
              <Rocket size={14} /> Upgrade Plan
            </button>
            <button
              onClick={() => setView("credits")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-md text-xs font-medium transition-colors ${
                view === "credits" ? "bg-[#FF4400] text-white" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              }`}
              data-testid="upgrade-tab-credits"
            >
              <Coins size={14} /> Buy Credits
            </button>
          </div>
        )}

        {/* Plans view */}
        {view === "plans" && (
          <>
            <div className="px-6 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {UPGRADE_TIERS.map((tier) => (
                <motion.div
                  key={tier.code}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-xl border p-4 ${
                    tier.highlight
                      ? "border-[#FF4400]/40 bg-[var(--accent-subtle)]"
                      : "border-[var(--border)] bg-[var(--bg-base)]"
                  }`}
                  data-testid={`upgrade-tier-${tier.code}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <tier.icon size={18} weight="fill" className={tier.highlight ? "text-[#FF4400]" : "text-[var(--text-muted)]"} />
                    <span className="font-['Cormorant_Garamond'] text-sm font-bold text-[var(--text-primary)]">{tier.label}</span>
                    <span className="ml-auto font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">${tier.price}<span className="text-xs text-[var(--text-muted)] font-normal">/mo</span></span>
                  </div>
                  <ul className="space-y-1.5">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        <Check size={12} weight="bold" className={tier.highlight ? "text-[#FF4400]" : "text-[var(--text-muted)]"} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>

            <div className="px-6 pb-6 space-y-2">
              <Button
                onClick={() => { onClose(); navigate("/pricing"); }}
                className="w-full bg-[#FF4400] hover:bg-[#E63E00] text-white gap-2"
                data-testid="upgrade-view-plans-btn"
              >
                View All Plans <ArrowRight size={14} />
              </Button>
              <button
                onClick={onClose}
                className="w-full py-2 text-xs text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-colors"
                data-testid="upgrade-dismiss-btn"
              >
                Maybe later
              </button>
            </div>
          </>
        )}

        {/* Credits view */}
        {view === "credits" && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {CREDIT_PACKS.map((pack) => (
                <button
                  key={pack.id}
                  disabled={!!purchasing}
                  onClick={() => handleBuyCredits(pack)}
                  className={`rounded-xl border p-3 text-left transition-all hover:border-[#FF4400]/40 ${
                    purchasing === pack.id ? "border-[#FF4400] bg-[var(--accent-subtle)]" : "border-[var(--border)] bg-[var(--bg-base)]"
                  } disabled:opacity-60`}
                  data-testid={`credit-pack-${pack.id}`}
                >
                  <div className="font-['Cormorant_Garamond'] text-lg font-bold text-[var(--text-primary)]">
                    {pack.credits.toLocaleString()}
                  </div>
                  <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider mb-2">credits</div>
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-bold text-[#FF4400]">${pack.price}</span>
                    <span className="text-[10px] text-[var(--text-muted)]">one-time</span>
                  </div>
                  {purchasing === pack.id && (
                    <div className="text-[10px] text-[#FF4400] mt-1 animate-pulse">Redirecting...</div>
                  )}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-muted)] text-center mb-3">
              Monthly allowance is used first. Purchased credits roll over and expire after 6 months (oldest used first). Max 2 packs/month. <a href="/legal/credits" className="text-[#FF4400] hover:underline">Full credit policy</a>.
            </p>
            <p className="text-xs text-center text-[var(--text-secondary)]">
              For consistent access, <button onClick={() => setView("plans")} className="text-[#FF4400] hover:underline font-medium">upgrade your plan</button> instead.
            </p>
          </div>
        )}

        <button
          onClick={() => { onClose(); setView("plans"); }}
          className="absolute top-3 right-3 p-1.5 rounded-md text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
        >
          <X size={16} />
        </button>
      </DialogContent>
    </Dialog>
  );
};
